<template>
  <div>
    <h4>Log a New Claim</h4>
    <form @submit.prevent="handleSubmit">

      <!-- Block 1 -->
      <b-card class="mb-3" no-body>
        <template #header><strong class="text-uppercase">Classification</strong></template>
        <b-card-body>
          <validated-multiselect id="category" label="Category" v-model="form.category" :options="categoryOptions" :errors="errors.category ? [errors.category] : []" />
          <div :class="{ 'field-disabled': !form.category }">
            <validated-multiselect id="subcategory" label="Subcategory" v-model="form.subcategory" :options="subcategoryOptions" :errors="errors.subcategory ? [errors.subcategory] : []" />
          </div>
          <div :class="['severity-select', optionClass(severityOptions, form.severity)]">
            <validated-multiselect id="severity" label="Severity" v-model="form.severity" :options="severityOptions" :errors="errors.severity ? [errors.severity] : []" />
          </div>
          <div :class="{ 'field-disabled': !vehicles.length }">
            <div v-if="vehicles.length === 1" class="form-group">
              <label>Vehicle</label>
              <div class="form-control-plaintext">{{ vehicles[0].year }} {{ vehicles[0].make }} {{ vehicles[0].model }}</div>
            </div>
            <validated-multiselect v-else id="vehicle" label="Vehicle" v-model="form.lead_vehicle_id" :options="vehicleOptions" :errors="errors.lead_vehicle_id ? [errors.lead_vehicle_id] : []" />
          </div>
          <div class="form-group">
            <label>Assigned Agent </label>
            <UserDropdown
              :value="form.assigned_user_id"
              :show-empty="false"
              @select="form.assigned_user_id = $event"
            />
            <div v-if="errors.assignedAgent" class="invalid-feedback d-block">{{ errors.assignedAgent }}</div>
          </div>
        </b-card-body>
      </b-card>

      <!-- Block 2 -->
        <b-card class="mb-3" no-body>
        <template #header><strong class="text-uppercase">Financial & Fault</strong></template>
        <b-card-body>
          <validated-input id="amount_paid" label="Amount Nexus Paid ($)" mask="price" v-model="form.amount_paid" :errors="errors.amount_paid ? [errors.amount_paid] : []" />
          <validated-input id="amount_to_recover" label="Amount to Recover ($)" mask="price" v-model="form.amount_to_recover" :errors="errors.amount_to_recover ? [errors.amount_to_recover] : []" />
          <validated-input id="repair_estimate" label="Repair Estimate ($)" mask="price" v-model="form.repair_estimate" :errors="errors.repair_estimate ? [errors.repair_estimate] : []" />
          <validated-multiselect id="faultParty" label="Fault Party" v-model="form.fault_party" :options="faultPartyOptions" :errors="errors.fault_party ? [errors.fault_party] : []" />
        </b-card-body>
      </b-card>

      <!-- Block 3 -->  
      <b-card class="mb-3" no-body>
        <template #header><strong class="text-uppercase">Documentation</strong></template>
        <b-card-body>
          <div :class="['yesno-select', optionClass(photosOptions, form.photos_received)]">
            <validated-multiselect id="photosReceived" label="Photos Received" v-model="form.photos_received" :options="photosOptions" :errors="errors.photos_received ? [errors.photos_received] : []" />
          </div>
          <div :class="['yesno-select', optionClass(yesNoOptions, form.bol_documented)]">
            <validated-multiselect id="bolDocumented" label="BOL Documented" v-model="form.bol_documented" :options="yesNoOptions" :errors="errors.bol_documented ? [errors.bol_documented] : []" />
          </div>
          <validated-multiselect id="slaDays" label="SLA Target" v-model="form.sla_days" :options="slaOptions" :errors="errors.sla_days ? [errors.sla_days] : []" />
          <div :class="['yesno-select', optionClass(yesNoOptions, form.insurance_involved)]">
            <validated-multiselect id="insuranceInvolved" label="Insurance Involved" v-model="form.insurance_involved" :options="yesNoOptions" :errors="errors.insurance_involved ? [errors.insurance_involved] : []" />
          </div>
          
          <validated-input id="emailThreadUrl" label="Email Thread URL (optional)" v-model="form.emailThreadUrl" />
          <validated-input id="description" label="Description / Initial Notes" type="textarea" v-model="form.description" :errors="errors.description ? [errors.description] : []" />
        </b-card-body>
      </b-card>
      
      <div class="d-flex justify-content-end mt-3" style="gap: 8px;">
        <b-button variant="outline-secondary" @click="$router.back()">Cancel</b-button>
        <b-button variant="primary" type="submit">Save</b-button>
      </div>
    </form>
  </div>
</template>

<script>
import { ApiClient } from '@/api/ApiClient';
import ValidatedMultiselect from '@/components/shared/ValidatedMultiselect.vue';
import ValidatedInput from '@/components/shared/ValidatedInput.vue';
import UserDropdown from '@/components/shared/UserDropdown';
import { claimSubcategories, claimCategoryLabels, claimSeverityOptions, claimFaultPartyOptions, claimPhotosOptions, claimBooleanOptions } from '@/helpers/claimOptions';

export default {
  name: 'OrderClaimNew',
  components: { ValidatedMultiselect, ValidatedInput, UserDropdown },
  data() {
    return {
      errors: {},
      vehicles: [],
      form: {
        category: null,
        subcategory: null,
        severity: 'medium',
        fault_party: null,
        sla_days: 14,
        bol_documented: null,
        photos_received: null,
        insurance_involved: null,
        amount_paid: null,
        repair_estimate: null,
        amount_to_recover: null,
        description: '',
        emailThreadUrl: '',
        lead_vehicle_id: null,
        assigned_user_id: null,
      },
    };
  },
  computed: {
    categoryOptions() {
      return [
        { value: null, label: '— Select —' },
        ...Object.keys(claimSubcategories).map(key => ({ value: key, label: claimCategoryLabels[key] })),
      ];
    },
    subcategoryOptions() {
      const base = [{ value: null, label: '— Select —' }];
      if (!this.form.category) return base;
      return [...base, ...claimSubcategories[this.form.category].map(s => ({ value: s.value, label: s.label }))];
    },
    severityOptions() {
      return [{ value: null, label: '— Select —' }, ...claimSeverityOptions];
    },
    faultPartyOptions() {
      return [{ value: null, label: '— Select —' }, ...claimFaultPartyOptions];
    },
    slaOptions() {
      return [
        { value: 7, label: '7 days' },
        { value: 14, label: '14 days' },
        { value: 30, label: '30 days' },
        { value: 60, label: '60 days' },
      ];
    },
    yesNoOptions() {
      return [{ value: null, label: '— Select —' }, ...claimBooleanOptions];
    },
    photosOptions() {
      return [{ value: null, label: '— Select —' }, ...claimPhotosOptions];
    },
    vehicleOptions() {
      const base = [{ value: null, label: '— Select vehicle —' }];
      return [...base, ...this.vehicles.map(v => ({ value: v.id, label: `${v.year} ${v.make} ${v.model}` }))];
    },
  },
  watch: {
    'form.category'() {
      this.form.subcategory = null;
    },
    'form.amount_paid'(newVal, oldVal) {
      if (this.form.amount_to_recover === oldVal || this.form.amount_to_recover === null) {
        this.form.amount_to_recover = newVal;
      }
    },
  },
  async created() {
    this.form.assigned_user_id = this.$store.state.auth.user?.id ?? null;
    const vehicleRes = await ApiClient.leads.vehicles.get({ leadId: this.$route.params.id });
    this.vehicles = vehicleRes.data.data || [];
    if (this.vehicles.length === 1) {
      this.form.lead_vehicle_id = this.vehicles[0].id;
    }
  },
  methods: {
    optionClass(options, value) {
      const opt = options.find(o => o.value === value);
      return opt && opt.colorClass ? opt.colorClass : '';
    },
    validate() {
      const errors = {};
      if (!this.form.category) errors.category = 'Category is required';
      if (!this.form.subcategory) errors.subcategory = 'Subcategory is required';
      if (!this.form.severity) errors.severity = 'Severity is required';
      if (this.vehicles.length > 1 && !this.form.lead_vehicle_id) errors.lead_vehicle_id = 'Vehicle is required';
      if (!this.form.assigned_user_id) errors.assignedAgent = 'Assigned agent is required';
      if (!this.form.photos_received) errors.photos_received = 'Photos received is required';
      if (this.form.bol_documented === null) errors.bol_documented = 'BOL documented is required';
      if (this.form.sla_days === null) errors.sla_days = 'SLA target is required';
      if (!this.form.description.trim()) errors.description = 'Description is required';
      this.errors = errors;
      return Object.keys(errors).length === 0;
    },
    async handleSubmit() {
      if (!this.validate()) return;
      const body = { ...this.form };
      if (body.amount_paid === null || body.amount_paid === '') delete body.amount_paid;
      if (body.amount_to_recover === null || body.amount_to_recover === '') delete body.amount_to_recover;
      if (body.repair_estimate === null || body.repair_estimate === '') delete body.repair_estimate;
      if (body.fault_party === null) delete body.fault_party;
      if (body.insurance_involved === null) delete body.insurance_involved;
      const response = await ApiClient.leads.claims.post({
        leadId: this.$route.params.id,
        body,
      });
      if (response.status === 200 || response.status === 201) {
        const newId = (response.data.data || response.data).id;
        this.$router.push({ name: 'OrderClaims', params: { id: this.$route.params.id }, query: newId ? { newClaimId: newId } : undefined });
      } else {
        this.errors = response.data?.errors || { generalError: response.data?.message || 'An error occurred.' };
      }
    },
  },
};
</script>

<style>
.severity-select .multiselect,
.yesno-select .multiselect { color: inherit !important; }

.severity-select label,
.yesno-select label { color: #4E5155; }
.dark-style .severity-select label,
.dark-style .yesno-select label { color: #fff; }

.field-disabled {
  pointer-events: none;
  opacity: 0.5;
}
</style>
